import firebaseApp from "../firebase/credenciales";
import { 
  getFirestore, 
  collection, 
  doc, 
  setDoc, 
  writeBatch, 
  serverTimestamp 
} from 'firebase/firestore';

const db = getFirestore(firebaseApp);

/**
 * Envía un correo electrónico individual
 */
export default async function sendCustomEmail(email, cc, bcc, subject, body, coleccion) {
  try {
    // Obtener fecha local
    const date = new Date();
    
    const fecha = `${date.getFullYear()}${
      (date.getMonth() + 1).toString().padStart(2, '0')}${
      date.getDate().toString().padStart(2, '0')}_${
      date.getHours().toString().padStart(2, '0')}${
      date.getMinutes().toString().padStart(2, '0')}${
      date.getSeconds().toString().padStart(2, '0')}_${
      date.getMilliseconds().toString().padStart(3, '0')}`;

    const collectionRef = collection(db, coleccion);
    const docuRef = doc(collectionRef, fecha);
    
    const emailContent = {
      to: email,
      ...(cc ? { cc } : {}),
      ...(bcc ? { bcc } : {}),
      message: {
        subject: subject,
        text: body,
        html: `<p>${body}</p>`,
      },
      status: 'pending',
      created: serverTimestamp(),
      attempts: 0
    };

    await setDoc(docuRef, emailContent);
    
    // Retornamos un objeto con el id para mantener compatibilidad
    return { 
      id: fecha,
      ref: docuRef,
      success: true
    };

  } catch (error) {
    console.error("Error en sendCustomEmail:", error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Función optimizada para envío masivo de correos en lotes
 * @param {Array} emails - Array de objetos con datos de correo
 * @param {string} coleccion - Nombre de la colección en Firestore
 * @param {number} batchSize - Tamaño del lote (máximo 500 para Firestore)
 */
export async function sendBulkEmails(emails, coleccion, batchSize = 400) {
  const results = {
    total: emails.length,
    success: 0,
    failed: 0,
    errors: []
  };

  try {
    // Dividir los correos en lotes para no exceder límites de Firestore
    for (let i = 0; i < emails.length; i += batchSize) {
      const batch = writeBatch(db);
      const currentBatch = emails.slice(i, i + batchSize);
      
      // Contador para mostrar progreso
      console.log(`Procesando lote ${Math.floor(i/batchSize) + 1} de ${Math.ceil(emails.length/batchSize)}`);
      
      // Procesar cada correo en el lote actual
      currentBatch.forEach((emailData, index) => {
        try {
          const date = new Date();
          date.setMilliseconds(date.getMilliseconds() + index); // Garantizar IDs únicos
          
          const fecha = `${date.getFullYear()}${
            (date.getMonth() + 1).toString().padStart(2, '0')}${
            date.getDate().toString().padStart(2, '0')}_${
            date.getHours().toString().padStart(2, '0')}${
            date.getMinutes().toString().padStart(2, '0')}${
            date.getSeconds().toString().padStart(2, '0')}_${
            date.getMilliseconds().toString().padStart(3, '0')}_${i + index}`;
          
          const docRef = doc(collection(db, coleccion), fecha);
          
          const emailContent = {
            to: emailData.email,
            cc: emailData.cc || null,
            bcc: emailData.bcc || null,
            message: {
              subject: emailData.subject,
              text: emailData.body,
              html: `<p>${emailData.body}</p>`,
            },
            metadata: emailData.metadata || {},
            status: 'pending',
            created: serverTimestamp(),
            attempts: 0,
            batchId: `batch_${Math.floor(i/batchSize)}`
          };
          
          batch.set(docRef, emailContent);
        } catch (err) {
          results.failed++;
          results.errors.push({
            email: emailData.email,
            error: err.message,
            phase: 'preparation'
          });
        }
      });
      
      // Ejecutar el lote
      try {
        await batch.commit();
        results.success += currentBatch.length - results.failed;
        
        // Pausa entre lotes para evitar sobrecargar Firestore
        if (i + batchSize < emails.length) {
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
      } catch (batchError) {
        console.error(`Error al procesar lote ${Math.floor(i/batchSize) + 1}:`, batchError);
        
        // Si falla el lote completo, intentar uno por uno (fallback)
        for (const emailData of currentBatch) {
          try {
            await sendCustomEmail(
              emailData.email, 
              emailData.cc, 
              emailData.bcc, 
              emailData.subject, 
              emailData.body, 
              coleccion
            );
            results.success++;
          } catch (singleError) {
            results.failed++;
            results.errors.push({
              email: emailData.email,
              error: singleError.message,
              phase: 'fallback'
            });
          }
          
          // Pequeña pausa entre envíos individuales
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }
    }
    
    return results;
    
  } catch (error) {
    console.error("Error general en sendBulkEmails:", error);
    results.errors.push({
      error: error.message,
      phase: 'general'
    });
    return results;
  }
}

/**
 * Envía correos en lotes con pausas controladas
 * @param {Array} emails - Array de objetos con datos de correo
 * @param {string} coleccion - Nombre de la colección en Firestore
 * @param {Object} options - Opciones de configuración
 */
export async function sendEmailsWithThrottling(emails, coleccion, options = {}) {
  const {
    batchSize = 50,           // Correos por lote
    pauseBetweenBatches = 3000, // Pausa entre lotes (ms)
    maxRetries = 3,           // Máximo de reintentos
    onProgress = null         // Función de callback para reportar progreso
  } = options;
  
  const results = {
    total: emails.length,
    processed: 0,
    success: 0,
    failed: 0,
    errors: []
  };
  
  // Dividir correos en lotes pequeños
  for (let i = 0; i < emails.length; i += batchSize) {
    const batch = emails.slice(i, i + batchSize);
    
    try {
      // Reportar progreso si hay callback
      if (onProgress) {
        onProgress({
          current: i,
          total: emails.length,
          percentage: Math.round((i / emails.length) * 100),
          batch: Math.floor(i / batchSize) + 1,
          totalBatches: Math.ceil(emails.length / batchSize)
        });
      }
      
      // Procesar lote actual
      const batchResult = await sendBulkEmails(batch, coleccion, batchSize);
      
      // Actualizar resultados
      results.processed += batch.length;
      results.success += batchResult.success;
      results.failed += batchResult.failed;
      results.errors = [...results.errors, ...batchResult.errors];
      
      // Pausa entre lotes para evitar sobrecargar el sistema
      if (i + batchSize < emails.length) {
        await new Promise(resolve => setTimeout(resolve, pauseBetweenBatches));
      }
    } catch (error) {
      console.error(`Error procesando lote desde ${i} hasta ${i + batchSize}:`, error);
      results.errors.push({
        startIndex: i,
        endIndex: i + batchSize,
        error: error.message
      });
    }
  }
  
  return results;
}